// Version: 2.0.0 - SSO login support + Preferences UI
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Mail, Lock, ArrowLeft, Upload, Loader2, 
  CheckCircle, AlertCircle, Camera, Edit2, Save, X,
  Plus, Trash2, Sparkles, PenLine, Brain
} from 'lucide-react';
import { getCurrentUser, getUserEmail, updatePassword, resetPassword, getUserProvider, getProviderDisplayName, isEmailPasswordUser } from '../../../shared/services/authService';
import { 
  getCurrentUserProfile, 
  updateDisplayName, 
  uploadAvatar, 
  deleteAvatar 
} from '../../../shared/services/profileService';
import type { UserProfile } from '../../../shared/services/profileService';
import {
  fetchPreferences,
  createPreference,
  updatePreference,
  deletePreference,
} from '../../../shared/services/preferencesService';
import type { Preference, PreferencesData, PreferenceType } from '../../../shared/services/preferencesService';
import { ForgetPasswordModal } from '../components/ForgetPasswordModal';
import { logger } from '../../../shared/utils/logger';

export function MyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // P2 修复：明确类型，替代 any（使用 SupabaseUser 别名避免与 Lucide User 图标冲突）
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Avatar state
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarSuccess, setAvatarSuccess] = useState(false);
  
  // Display name state
  const [displayName, setDisplayName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSuccess, setNameSuccess] = useState(false);
  
  // Password state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Forget password modal
  const [showForgetPasswordModal, setShowForgetPasswordModal] = useState(false);

  // 2.0.0: 偏好 UI 状态
  const [prefsData, setPrefsData] = useState<PreferencesData | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);
  const [newPrefText, setNewPrefText] = useState('');
  const [newPrefType, setNewPrefType] = useState<PreferenceType>('preference');
  const [isAddingPref, setIsAddingPref] = useState(false);
  const [editingPrefId, setEditingPrefId] = useState<string | null>(null);
  const [editingPrefText, setEditingPrefText] = useState('');

  /**
   * 2.0.0: 加载用户偏好数据
   * 从后端 /api/user/preferences 获取分组偏好
   */
  const loadPreferences = useCallback(async (userId: string) => {
    setPrefsLoading(true);
    setPrefsError(null);
    try {
      const data = await fetchPreferences(userId);
      setPrefsData(data);
    } catch (err: unknown) {
      logger.error('加载偏好失败:', err);
      setPrefsError(err instanceof Error ? err.message : t('prefLoadError'));
    } finally {
      setPrefsLoading(false);
    }
  }, [t]);

  /**
   * 2.0.0: 添加新偏好
   */
  const handleAddPreference = async () => {
    if (!user || !newPrefText.trim()) return;
    setIsAddingPref(true);
    try {
      await createPreference(user.id, newPrefText.trim(), newPrefType);
      setNewPrefText('');
      // 重新加载偏好列表
      await loadPreferences(user.id);
    } catch (err: unknown) {
      logger.error('添加偏好失败:', err);
    } finally {
      setIsAddingPref(false);
    }
  };

  /**
   * 2.0.0: 保存编辑后的偏好
   */
  const handleSavePreference = async (prefId: string) => {
    if (!user || !editingPrefText.trim()) return;
    try {
      await updatePreference(user.id, prefId, editingPrefText.trim());
      setEditingPrefId(null);
      setEditingPrefText('');
      await loadPreferences(user.id);
    } catch (err: unknown) {
      logger.error('编辑偏好失败:', err);
    }
  };

  /**
   * 2.0.0: 删除偏好
   */
  const handleDeletePreference = async (prefId: string) => {
    if (!user) return;
    try {
      await deletePreference(user.id, prefId);
      await loadPreferences(user.id);
    } catch (err: unknown) {
      logger.error('删除偏好失败:', err);
    }
  };

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        // Redirect to home if not logged in
        navigate('/');
        return;
      }
      
      setUser(currentUser);
      
      // Load profile
      const { profile: userProfile, error: profileError } = await getCurrentUserProfile();
      if (profileError) {
        logger.error('Error loading profile:', profileError);
      }
      
      if (userProfile) {
        setProfile(userProfile);
        setDisplayName(userProfile.display_name || getUserEmail(currentUser) || '');
      } else {
        // Initialize with email if no profile exists
        setDisplayName(getUserEmail(currentUser) || '');
      }

      // 2.0.0: 加载用户偏好（异步，不阻塞页面加载）
      loadPreferences(currentUser.id);
    } catch (err: unknown) {
      logger.error('Error loading user data:', err);
      setError(err instanceof Error ? err.message : '加载用户数据失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setIsUploadingAvatar(true);
    setAvatarError(null);
    setAvatarSuccess(false);

    try {
      // Delete old avatar if exists
      if (profile?.avatar_url) {
        await deleteAvatar(profile.avatar_url);
      }

      const { url, error: uploadError } = await uploadAvatar(user.id, file);
      
      if (uploadError) {
        setAvatarError(uploadError);
        return;
      }

      if (url) {
        // Reload profile to get updated avatar URL
        const { profile: updatedProfile } = await getCurrentUserProfile();
        if (updatedProfile) {
          setProfile(updatedProfile);
        }
        setAvatarSuccess(true);
        setTimeout(() => setAvatarSuccess(false), 3000);
      }
    } catch (err: unknown) {
      logger.error('Error uploading avatar:', err);
      setAvatarError(err instanceof Error ? err.message : '上传头像失败');
    } finally {
      setIsUploadingAvatar(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleSaveName = async () => {
    if (!user) return;

    setIsSavingName(true);
    setNameError(null);
    setNameSuccess(false);

    try {
      const { error: updateError } = await updateDisplayName(user.id, displayName);
      
      if (updateError) {
        setNameError(updateError);
        return;
      }

      // Reload profile
      const { profile: updatedProfile } = await getCurrentUserProfile();
      if (updatedProfile) {
        setProfile(updatedProfile);
      }
      
      setNameSuccess(true);
      setIsEditingName(false);
      setTimeout(() => setNameSuccess(false), 3000);
    } catch (err: unknown) {
      logger.error('Error updating display name:', err);
      setNameError(err instanceof Error ? err.message : '更新名称失败');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setPasswordError(t('passwordMismatch'));
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError(t('passwordTooShort'));
      return;
    }

    setIsChangingPassword(true);
    setPasswordError(null);
    setPasswordSuccess(false);

    try {
      const { error: updateError } = await updatePassword(oldPassword, newPassword);
      
      if (updateError) {
        setPasswordError(updateError.message);
        return;
      }

      setPasswordSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err: unknown) {
      logger.error('Error changing password:', err);
      setPasswordError(err instanceof Error ? err.message : '修改密码失败');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleForgetPassword = async () => {
    if (!user?.email) return;

    setShowForgetPasswordModal(true);
  };

  const handleSendResetLink = async (email: string) => {
    try {
      logger.log('📧 Attempting to send password reset link to:', email);
      const { error } = await resetPassword(email);
      if (error) {
        logger.error('❌ Failed to send reset link:', {
          email: email,
          error: error.message,
          code: error.code
        });
        return { error: error.message };
      }
      logger.log('✅ Password reset link sent successfully to:', email);
      return { error: null };
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '发送重置链接失败';
      logger.error('❌ Exception while sending reset link:', {
        email: email,
        error: errMsg,
        stack: err instanceof Error ? err.stack : undefined,
      });
      return { error: errMsg };
    }
  };

  if (isLoading) {
    return (
      <div className="h-full bg-white dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const userEmail = getUserEmail(user);
  const avatarUrl = profile?.avatar_url || null;
  const userProvider = getUserProvider(user);
  const providerDisplayName = getProviderDisplayName(userProvider);
  const isEmailUser = isEmailPasswordUser(user);

  /* 汇总全部偏好（用于渲染） */
  const allPrefs: Preference[] = prefsData
    ? [...(prefsData.preferences || []), ...(prefsData.restrictions || []), ...(prefsData.facts || [])]
    : [];

  return (
    <div className="h-full bg-white dark:bg-gray-950 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* 返回按钮 */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-orange-500 hover:text-orange-600 dark:hover:text-orange-400 text-sm mb-4 transition-colors"
          aria-label={t('back', '返回')}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('back', 'Return')}</span>
        </button>

        {/* 页面标题 — 使用 myPage key，对应多语言 */}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-8">
          {t('myPage')}
        </h1>

        {/* 错误提示 */}
        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </motion.div>
        )}

        {/* ===== 两列布局: 左侧头像 + 右侧信息 ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 mb-8">

          {/* ── 左侧: 头像区 ── */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex flex-col items-center">
            <div className="relative mb-4">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar"
                  className="w-36 h-36 sm:w-44 sm:h-44 rounded-full object-cover border-4 border-orange-500 shadow-lg" />
              ) : (
                <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center border-4 border-orange-500 shadow-lg">
                  <User className="w-16 h-16 text-white" />
                </div>
              )}
              {/* 相机图标 — 橙色圆圈（无外框）+ 白色机身 + 镜头透出橙色背景 */}
              <label htmlFor="avatar-upload"
                className="absolute bottom-1 right-1 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center cursor-pointer hover:bg-orange-600 transition-colors shadow-lg">
                <svg className="w-6 h-6" viewBox="0 0 24 24" aria-hidden="true">
                  {/* 相机机身 — 白色填充 */}
                  <path fill="white" d="M15.2 3.8C14.9 3.3 14.5 3 14 3h-4c-.5 0-.9.3-1.2.8L7.5 6H4C2.9 6 2 6.9 2 8v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-3.5l-1.3-2.2z" />
                  {/* 镜头 — 橙色填充（与背景同色，形成镂空效果） */}
                  <circle cx="12" cy="13" r="3.5" fill="#f97316" />
                  {/* 镜头内圈 — 白色小圆点（高光） */}
                  <circle cx="12" cy="13" r="1.2" fill="white" />
                </svg>
                <input id="avatar-upload" type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAvatarUpload} className="hidden" disabled={isUploadingAvatar} />
              </label>
            </div>
            {isUploadingAvatar && <div className="flex items-center gap-2 text-orange-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" />{t('loading')}</div>}
            {avatarError && <p className="text-red-500 text-xs mt-1">{avatarError}</p>}
            {avatarSuccess && <p className="text-green-500 text-xs mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" />{t('avatarUploaded')}</p>}
          </motion.div>

          {/* ── 右侧: 信息区（两列子布局: 个人信息 | 登录方式） ── */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-8">

            {/* 上半部分：个人信息 + 登录方式 并排 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">

              {/* Personal Information 区块（左列） */}
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white pb-2 mb-4 border-b-2 border-orange-500">
                  {t('personalInfo', 'Personal Information')}
                </h2>
                <div className="space-y-5">
                  {/* Username (Display Name) — 图标在右侧 */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('displayName', 'Username')}</p>
                      {isEditingName ? (
                        <div className="flex items-center gap-2 mt-1">
                          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setIsEditingName(false); setDisplayName(profile?.display_name || userEmail || ''); setNameError(null); } }}
                            className="flex-1 text-sm px-2 py-1 border border-orange-300 dark:border-orange-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:outline-none"
                            autoFocus />
                          <button onClick={handleSaveName} disabled={isSavingName} className="p-1 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/30 rounded">
                            {isSavingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </button>
                          <button onClick={() => { setIsEditingName(false); setDisplayName(profile?.display_name || userEmail || ''); setNameError(null); }}
                            className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{displayName || '—'}</p>
                      )}
                    </div>
                    {/* 编辑图标 — 实心填充橙色，参考截图风格 */}
                    {!isEditingName && (
                      <button onClick={() => setIsEditingName(true)}
                        className="p-1 flex-shrink-0 hover:opacity-70 transition-opacity"
                        aria-label={t('editName')}>
                        <Edit2 className="w-7 h-7 text-orange-500" fill="currentColor" strokeWidth={0.5} />
                      </button>
                    )}
                  </div>
                  {nameError && <p className="text-xs text-red-500">{nameError}</p>}
                  {nameSuccess && <p className="text-xs text-green-500 flex items-center gap-1"><CheckCircle className="w-3 h-3" />{t('nameUpdated')}</p>}

                  {/* Email — 实心填充橙色信封图标，参考截图风格 */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('userEmail', 'Email')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{userEmail}</p>
                    </div>
                    {/* 实心填充信封 SVG — 参考截图风格 */}
                    <svg className="w-7 h-7 text-orange-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M2 6a2 2 0 012-2h16a2 2 0 012 2v.217l-10 6.11L2 6.217V6zm0 2.383V18a2 2 0 002 2h16a2 2 0 002-2V8.383l-9.515 5.814a1 1 0 01-1.04-.025L2 8.383z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Login Methods 区块（右列，与个人信息并排） */}
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white pb-2 mb-4 border-b-2 border-orange-500">
                  {t('loginMethod', 'Login Methods')}
                </h2>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('currentLoginMethod', 'Current Login Method')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{providerDisplayName}</p>
                  </div>
                  {/* 根据登录方式动态显示对应图标 */}
                  {userProvider === 'google' ? (
                    /* Google 彩色 G logo，参考截图风格 */
                    <svg className="w-7 h-7 flex-shrink-0" viewBox="0 0 24 24" aria-label="Google">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  ) : (
                    /* 其他登录方式使用橙色圆形用户图标 */
                    <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <User className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                </div>
              </div>

              {/* 用户偏好区块 — 暂时隐藏，待 UI 完善后恢复 */}
              {/* <div className="md:col-span-1">
                <h2 className="text-base font-bold text-gray-900 dark:text-white pb-2 mb-4 border-b-2 border-orange-500">
                  {t('preferencesTitle', 'User Preferences')}
                </h2>

              {prefsLoading && <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>}
              {prefsError && <p className="text-sm text-red-500 mb-3 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{prefsError}</p>}

              {prefsData && !prefsLoading && (
                <>
                  {allPrefs.length === 0 ? (
                    <div className="text-center py-6">
                      <Sparkles className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('prefEmpty', '暂无偏好记录')}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('prefEmptyHint')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2 mb-4">
                      <AnimatePresence>
                        {allPrefs.map((pref: Preference) => (
                          <motion.div key={pref.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: -20 }}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 group hover:border-orange-300 dark:hover:border-orange-700 transition-colors">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                              pref.type === 'preference' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                              : pref.type === 'restriction' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            }`}>
                              {pref.type === 'preference' ? t('prefTypePreference') : pref.type === 'restriction' ? t('prefTypeRestriction') : t('prefTypeFact')}
                            </span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                              pref.source === 'ai' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400' : 'bg-green-100 dark:bg-green-900/30 text-green-500 dark:text-green-400'
                            }`}>{pref.source === 'ai' ? 'AI' : t('prefSourceManual')}</span>
                            <div className="flex-1 min-w-0">
                              {editingPrefId === pref.id ? (
                                <div className="flex items-center gap-1.5">
                                  <input type="text" value={editingPrefText} onChange={(e) => setEditingPrefText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreference(pref.id); if (e.key === 'Escape') { setEditingPrefId(null); setEditingPrefText(''); } }}
                                    className="flex-1 text-sm px-2 py-1 border border-orange-300 dark:border-orange-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-1 focus:ring-orange-500 focus:outline-none" autoFocus />
                                  <button onClick={() => handleSavePreference(pref.id)} className="p-1 text-green-500"><Save className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => { setEditingPrefId(null); setEditingPrefText(''); }} className="p-1 text-gray-400"><X className="w-3.5 h-3.5" /></button>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-800 dark:text-gray-200 truncate">{pref.text}</p>
                              )}
                            </div>
                            {editingPrefId !== pref.id && (
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                <button onClick={() => { setEditingPrefId(pref.id); setEditingPrefText(pref.text); }}
                                  className="p-1 text-gray-400 hover:text-orange-500 rounded"><PenLine className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleDeletePreference(pref.id)}
                                  className="p-1 text-gray-400 hover:text-red-500 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700/50">
                    <select value={newPrefType} onChange={(e) => setNewPrefType(e.target.value as PreferenceType)}
                      className="text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 focus:ring-1 focus:ring-orange-500 focus:outline-none">
                      <option value="preference">{t('prefTypePreference')}</option>
                      <option value="restriction">{t('prefTypeRestriction')}</option>
                      <option value="fact">{t('prefTypeFact')}</option>
                    </select>
                    <input type="text" value={newPrefText} onChange={(e) => setNewPrefText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && newPrefText.trim()) handleAddPreference(); }}
                      placeholder={t('prefAddPlaceholder', '输入新的偏好...')}
                      className="flex-1 text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:outline-none" />
                    <button onClick={handleAddPreference} disabled={!newPrefText.trim() || isAddingPref}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-orange-500 border border-dashed border-orange-300 dark:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded disabled:opacity-40 transition-colors">
                      {isAddingPref ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      <span className="hidden sm:inline">{t('prefAdd', '添加')}</span>
                    </button>
                  </div>
                </>
              )}
            </div> */}

            </div>{/* 关闭 grid 容器 (line 405) */}

          </motion.div>
        </div>

        {/* ===== 密码修改 (仅邮箱登录用户) ===== */}
        {isEmailUser && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 mb-6">
            <h2 className="text-base font-bold text-gray-900 dark:text-white pb-2 mb-4 border-b-2 border-orange-500">{t('changePassword')}</h2>
            <form onSubmit={handleChangePassword} className="space-y-3 max-w-md">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input id="old-password" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  placeholder={t('oldPassword')} required />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  placeholder={t('newPassword')} minLength={6} required />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  placeholder={t('confirmPassword')} minLength={6} required />
              </div>
              {passwordError && <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{passwordError}</p>}
              {passwordSuccess && <p className="text-sm text-green-500 flex items-center gap-1"><CheckCircle className="w-4 h-4" />{t('passwordUpdated')}</p>}
              <div className="flex items-center gap-3 pt-1">
                <button type="submit" disabled={isChangingPassword}
                  className="px-4 py-2 text-sm bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-lg disabled:opacity-50 flex items-center gap-2">
                  {isChangingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                  {t('updatePassword')}
                </button>
                <button type="button" onClick={handleForgetPassword}
                  className="text-sm text-orange-500 hover:text-orange-600 dark:hover:text-orange-400">{t('forgotPassword')}</button>
              </div>
            </form>
          </motion.div>
        )}
      </div>

      {/* 忘记密码弹窗 */}
      <ForgetPasswordModal isOpen={showForgetPasswordModal} onClose={() => setShowForgetPasswordModal(false)}
        userEmail={userEmail || ''} onSendResetLink={handleSendResetLink} />
    </div>
  );
}

